import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import { Combobox } from '@/components'
import { Checkbox } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { IssueFormMode, IssueRelationConfigsQuery } from '../field-config'
import { ISSUE_RELATION_CONFIGS_QUERY } from '../field-config'
import { ALL_ISSUE_RELATIONS_QUERY } from '../queries'

interface RelationOptionNode {
  id: string
  name: string
}

interface RelationNode {
  id: string
  name: string
  multipleChoice: boolean
  visibleOnCreate: boolean
  requiredOnCreate: boolean
  visibleOnEdit: boolean
  requiredOnEdit: boolean
  options: { edges: { node: RelationOptionNode }[] }
}

interface RelationFlags {
  visibleOnCreate: boolean
  requiredOnCreate: boolean
  visibleOnEdit: boolean
  requiredOnEdit: boolean
}

const isVisible = (flags: RelationFlags, mode: IssueFormMode) =>
  mode === 'create' ? flags.visibleOnCreate : flags.visibleOnEdit

const isRequired = (flags: RelationFlags, mode: IssueFormMode) =>
  mode === 'create' ? flags.requiredOnCreate : flags.requiredOnEdit

interface AllIssueRelationsData {
  allIssueRelations: { edges: { node: RelationNode }[] }
}

/**
 * Relations as they apply to `categoryId` — visibility and requiredness come
 * from the per-category configuration, the options from the relations
 * themselves. A relation the category has no override for resolves to the
 * company standard, so passing no category is the standard form.
 */
const useResolvedRelations = (categoryId?: string | null) => {
  const { data: relationData } = useQuery<AllIssueRelationsData>(
    ALL_ISSUE_RELATIONS_QUERY
  )
  const { data: configData, loading } = useQuery<IssueRelationConfigsQuery>(
    ISSUE_RELATION_CONFIGS_QUERY,
    { variables: { category: categoryId || null } }
  )

  const flagsByRelation = new Map<string, RelationFlags>()
  for (const config of configData?.issueRelationConfigs ?? [])
    flagsByRelation.set(config.relation, config)

  const relations = (relationData?.allIssueRelations.edges ?? []).map(edge => ({
    ...edge.node,
    ...(flagsByRelation.get(edge.node.id) ?? edge.node),
  }))

  return { relations, loading }
}

/**
 * The issue form's relation value: relationId -> chosen option ids. Always a
 * list, so a single-choice relation is just the case where at most one id
 * fits.
 */
export type IssueRelationValue = Record<string, string[]>

/**
 * Returns true if every relation the company requires *for this mode* has at
 * least one option chosen in `value`. A relation hidden for the mode is never
 * required in it. Returns true while relations are still loading so the UI
 * does not flash "invalid" before data arrives.
 */
export const useRequiredRelationsSatisfied = (
  value: IssueRelationValue,
  mode: IssueFormMode,
  categoryId?: string | null
): boolean => {
  const { relations, loading } = useResolvedRelations(categoryId)
  if (loading) return true
  return relations
    .filter(r => isVisible(r, mode) && isRequired(r, mode))
    .every(r => (value[r.id]?.length ?? 0) > 0)
}

/**
 * Render a single relation as an input. Caller wraps it in a grid cell so the
 * visual style matches surrounding fields.
 *
 * A multiple choice relation lists its options as checkboxes — every option
 * is visible at once, which is the point of the field type. A single choice
 * relation keeps the dropdown, clearable unless the company requires the
 * relation for this mode: a required relation must always keep a value, an
 * optional one must be possible to unpick again.
 */
interface RelationFieldProps {
  relation: RelationNode
  value: string[]
  mode: IssueFormMode
  onChange(optionIds: string[]): void
}

export const IssueRelationField: React.FC<RelationFieldProps> = ({
  relation,
  value,
  mode,
  onChange,
}) => {
  const translations = useTranslate({ select: 'common.select' })

  const options = relation.options.edges.map(e => e.node)

  if (relation.multipleChoice) {
    return (
      <div className="flex flex-col gap-2">
        {options.map(option => {
          const inputId = `relation-${relation.id}-option-${option.id}`
          const checked = value.includes(option.id)

          return (
            <label
              key={option.id}
              htmlFor={inputId}
              className="flex cursor-pointer items-center gap-2 text-sm text-stone-800"
            >
              <Checkbox
                id={inputId}
                value={checked}
                onChange={next =>
                  onChange(
                    next === true
                      ? [...value, option.id]
                      : value.filter(id => id !== option.id)
                  )
                }
              />
              {option.name}
            </label>
          )
        })}
      </div>
    )
  }

  return (
    <Combobox
      mode="single"
      value={value[0] ?? ''}
      items={options.map(option => ({
        label: option.name,
        value: option.id,
      }))}
      clearable={!isRequired(relation, mode)}
      placeholder={translations.select}
      onChange={id => onChange(id ? [id] : [])}
    />
  )
}

interface IssueRelationsPanelProps {
  /** Current form value: relationId -> chosen option ids. */
  value: IssueRelationValue
  /** Render inputs when true, read-only values when false. */
  editable: boolean
  /** Which half of the flow — decides visibility and the required mark. */
  mode: IssueFormMode
  /** The issue's category; decides which relations apply and how. */
  categoryId?: string | null
  onChange(relationId: string, optionIds: string[]): void
  className?: string
}

/**
 * Renders every IssueRelation the company shows for this mode — required and
 * optional alike — so the user can see what an issue can be linked to before
 * linking anything. Linked relations show their chosen options; unlinked ones
 * show an empty state (read mode) or an inline input (edit mode).
 */
export const IssueRelationsPanel: React.FC<IssueRelationsPanelProps> = ({
  value,
  editable,
  mode,
  categoryId,
  onChange,
  className,
}) => {
  const translations = useTranslate({
    relations: 'issues.relations',
    notLinked: 'issues.not-linked',
  })
  const { relations: resolved } = useResolvedRelations(categoryId)
  const relations = resolved.filter(relation => isVisible(relation, mode))

  // Nothing to show if the company defined no relations, or hid them all
  // for this half of the flow.
  if (relations.length === 0) return null

  return (
    <section className={cn('rounded-2xl bg-white shadow-card', className)}>
      <header className="border-b border-stone-200 px-5 py-4">
        <h4 className="m-0 text-base font-semibold text-stone-900">
          {translations.relations}
        </h4>
      </header>
      <div className="divide-y divide-stone-200">
        {relations.map(relation => {
          const selectedIds = value[relation.id] ?? []
          const selectedNames = relation.options.edges
            .map(e => e.node)
            .filter(option => selectedIds.includes(option.id))
            .map(option => option.name)

          return (
            <div
              key={relation.id}
              className="flex flex-col gap-1.5 px-5 py-3.5"
            >
              <span className="flex items-center gap-1 text-xs font-semibold uppercase text-stone-500">
                {relation.name}
                {isRequired(relation, mode) && (
                  <span className="text-rose-500">*</span>
                )}
              </span>
              {editable ? (
                <IssueRelationField
                  relation={relation}
                  value={selectedIds}
                  mode={mode}
                  onChange={ids => onChange(relation.id, ids)}
                />
              ) : selectedNames.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {selectedNames.map(name => (
                    <span
                      key={name}
                      className="inline-flex w-fit items-center gap-2 rounded-full border border-stone-300 bg-stone-100 px-3 py-1 text-sm font-semibold text-stone-800"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-stone-500" />
                      {name}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="inline-flex w-fit items-center gap-2 rounded-full border border-dashed border-stone-300 px-3 py-1 text-sm text-stone-400">
                  {translations.notLinked}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

/** Build initial form value from an existing issue's relation_options edges. */
export const buildInitialRelationOptions = (
  edges?: { node: { id: string; relation: { id: string } } }[]
): IssueRelationValue => {
  const out: IssueRelationValue = {}
  for (const edge of edges ?? []) {
    const chosen = out[edge.node.relation.id] ?? []
    chosen.push(edge.node.id)
    out[edge.node.relation.id] = chosen
  }
  return out
}

/** Build mutation payload: list of selected IssueRelationOption ids. */
export const serializeRelationOptions = (value: IssueRelationValue): string[] =>
  Object.values(value).flat()
